// Directory tree panel: folder navigation for the opened root, plus (in later
// sections of this module) the folder context menu, cut/paste + rename via a
// recursive per-file move (FileSystemDirectoryHandle has no move() in any
// browser), moving selected media between folders, and recursive thumbnail
// generation. Everything tree-shaped lives here; app.js passes in the shared
// state and the openDir navigation entry point.

import { settings } from './settings.js';
import { moveThumb, ensureThumb } from './thumbs.js';
import { IMG, VID, SORT_FILE, HEADER, icon } from './util.js';

const TREE_FRAC_MIN = 0.1, TREE_FRAC_MAX = 0.5, TREE_FRAC_DEFAULT = 0.22;
// 500ms in production (matches selection.js's long-press feel); a test harness
// may lower it via the timing override (see js/app.js's central definition).
const LONG_PRESS_MS = globalThis.__MS_TEST_TIMINGS__?.longPressMs ?? 500, MOVE_TOL = 10;
const BAD_NAME = /[\/\\:*?"<>|]/;

function samePath(a, b) { return a.length === b.length && a.every((s, i) => s === b[i]); }
// Is `prefix` an ancestor of (or equal to) `path`?
function isPrefix(prefix, path) { return prefix.length <= path.length && prefix.every((s, i) => s === path[i]); }
// Rewrite `cur` after the subtree at `oldP` moved to `newP` (no-op otherwise).
function remapPath(cur, oldP, newP) { return isPrefix(oldP, cur) ? [...newP, ...cur.slice(oldP.length)] : cur; }

/* ---------- Filesystem helpers (pure — no DOM, no module state) ---------- */
// Only NotFoundError means "free"; TypeMismatchError means an entry of the
// other kind is there (still taken); anything else (permission hiccup etc.)
// propagates rather than being read as "free".
async function nameTaken(dir, name) {
  try { await dir.getFileHandle(name); return true; }
  catch (e) {
    if (e?.name === 'TypeMismatchError') return true;
    if (e?.name !== 'NotFoundError') throw e;
  }
  try { await dir.getDirectoryHandle(name); return true; }
  catch (e) {
    if (e?.name === 'TypeMismatchError') return true;
    if (e?.name !== 'NotFoundError') throw e;
  }
  return false;
}

// First free variant of `name` in `dir`: the name itself, then " (2)", " (3)"…
// inserted before the extension. Files and folders both count as taken.
export async function uniqueName(dir, name) {
  if (!(await nameTaken(dir, name))) return name;
  const m = /^(.*?)(\.[^.]*)?$/.exec(name);
  for (let i = 2; ; i++) {
    const cand = `${m[1]} (${i})${m[2] || ''}`;
    if (!(await nameTaken(dir, cand))) return cand;
  }
}

// Move one file into destDir under newName (caller guarantees uniqueness).
// Chromium supports cross-directory FileSystemFileHandle.move; the copy+delete
// fallback changes mtime, which the thumb-key migration absorbs. The fallback
// is the COMMON path on real local folders (Chromium has only shipped
// cross-directory move for OPFS), so it must be safe for real data:
// - never overwrite: handle.move errors on an existing target, but
//   getFileHandle(create:true) would silently truncate — probe first, so a
//   TOCTOU'd uniqueName (an external file manager racing us) fails loudly
//   instead of eating a file. Only NotFoundError reads as "free" — a
//   TypeMismatchError (a directory sits there) or any other probe error
//   also fails loudly rather than risk a silent overwrite;
// - stream, don't buffer: arrayBuffer() of a multi-GB video OOMs the tab
//   mid-move; pipeTo copies chunkwise and closes the writable.
// - no stub on failure: getFileHandle(create:true) makes the 0-byte target
//   before the copy runs, so a stream/createWritable failure is cleaned up
//   (best-effort removeEntry, since the probe just proved we own this name)
//   before the original error is rethrown — otherwise a retried moveDir hits
//   "already exists" against our own empty leftover forever.
export async function moveFile(handle, srcDir, destDir, newName) {
  if (handle.move) {
    try { await handle.move(destDir, newName); return handle; } catch {}
  }
  const file = await handle.getFile();
  if (await nameTaken(destDir, newName)) throw new Error(`"${newName}" already exists in the destination`);
  const nh = await destDir.getFileHandle(newName, { create: true });
  try {
    await file.stream().pipeTo(await nh.createWritable());
  } catch (e) {
    try { await destDir.removeEntry(newName); } catch {}
    throw e;
  }
  await srcDir.removeEntry(handle.name);
  return nh;
}

// Recursive per-file directory move — no browser implements move() on
// directory handles, so this is THE folder rename/relocate primitive. Moves
// every entry (media, .XnViewSort, dotfiles, non-media — nothing is dropped),
// then removes the emptied source. The entry list is snapshotted before any
// mutation: iterating a directory while removing from it is undefined
// behavior. `onProgress(n)` fires per moved file. A mid-move failure throws
// and leaves the true partial state on disk for the caller to surface.
export async function moveDir(srcParent, srcName, destParent, destName, onProgress, counter = { n: 0 }) {
  const src = await srcParent.getDirectoryHandle(srcName);
  const dest = await destParent.getDirectoryHandle(destName, { create: true });
  const entries = [];
  for await (const e of src.entries()) entries.push(e);
  for (const [name, h] of entries) {
    if (h.kind === 'file') { await moveFile(h, src, dest, name); onProgress?.(++counter.n); }
    else await moveDir(src, name, dest, name, onProgress, counter);
  }
  await srcParent.removeEntry(srcName);
  return dest;
}

// Descend `segs` from `root`; null when any segment no longer resolves.
async function resolvePath(root, segs) {
  let cur = root;
  for (const s of segs) {
    try { cur = await cur.getDirectoryHandle(s); } catch { return null; }
  }
  return cur;
}

export function initTree({ state, grid, setStatus, writeSortFile, resetHistory, clearPreview, selection, purgeDragState, openDir, withExclusiveOp }) {
  const treeEl = document.getElementById('tree');
  const treeBody = document.getElementById('treeBody');
  const treeDivider = document.getElementById('treeDivider');
  const treeBtn = document.getElementById('treeBtn');

  let rootNode = null;

  /* ---------- Nodes ---------- */
  // Each .tnode carries its own handle + path segments; children are scanned
  // lazily on first expand (and by setCurrent walking down to the open folder).
  // An unscanned node shows the twist — we can't know it's a leaf until its
  // one-level child scan runs.
  function makeNode(name, dir, path) {
    const node = document.createElement('div');
    node.className = 'tnode';
    node._dir = dir; node._path = path; node._name = name;
    node._scanned = false; node._expanded = false;

    const row = document.createElement('div');
    row.className = 'trow';
    const twist = document.createElement('span');
    twist.className = 'twist'; twist.innerHTML = icon('chevron-right');
    const label = document.createElement('span');
    label.className = 'tname'; label.textContent = name;
    row.append(twist, label);
    row.title = name;

    const kids = document.createElement('div');
    kids.className = 'tkids'; kids.hidden = true;
    node.append(row, kids);
    return node;
  }
  const rowOf = (node) => node.querySelector(':scope > .trow');
  const kidsOf = (node) => node.querySelector(':scope > .tkids');

  async function scanNode(node) {
    const dirs = [];
    for await (const [name, h] of node._dir.entries()) {
      if (h.kind === 'directory') dirs.push([name, h]);
    }
    dirs.sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
    kidsOf(node).replaceChildren(...dirs.map(([name, h]) => makeNode(name, h, [...node._path, name])));
    node._scanned = true;
    node.classList.toggle('leaf', dirs.length === 0);
    return dirs.length;
  }

  function setExpanded(node, on) {
    node._expanded = on && !node.classList.contains('leaf');
    kidsOf(node).hidden = !node._expanded;
    rowOf(node).querySelector('.twist').innerHTML = icon(node._expanded ? 'chevron-down' : 'chevron-right');
  }
  async function toggleExpand(node) {
    if (!node._scanned) await scanNode(node);
    setExpanded(node, !node._expanded);
  }

  treeBody.addEventListener('click', async (e) => {
    if (suppressClick) { suppressClick = false; return; }   // trailing click of a long-press
    const row = e.target.closest('.trow');
    if (!row) return;
    const node = row.parentElement;
    if (e.target.closest('.twist')) { await toggleExpand(node); return; }
    if (!samePath(node._path, state.path)) await openDir(node._dir, node._path);
  });

  // Rescan one node's children in place (after an op created/renamed/removed a
  // child). Grandchildren state is rebuilt fresh — acceptable simplicity.
  async function refreshNode(node) {
    node._scanned = false;
    await scanNode(node);
    setExpanded(node, node._expanded);
  }
  // The .tnode whose .tkids contains `node` (rootNode's parent is null).
  const parentNodeOf = (node) => node.parentElement?.closest('.tnode') ?? null;

  /* ---------- Context menu ---------- */
  const menu = document.getElementById('treeMenu');
  let suppressClick = false; // long-press fired — swallow the trailing click

  function closeMenu() { menu.hidden = true; }

  function addItem(iconName, label, enabled, fn) {
    const b = document.createElement('button');
    b.innerHTML = icon(iconName);
    b.append(label);  // text node — label is app-controlled but keep it inert
    b.disabled = !enabled;
    b.onclick = () => { closeMenu(); fn(); };
    menu.appendChild(b);
  }

  function buildMenu(node) {
    menu.replaceChildren();
    const isRoot = node._path.length === 0;
    // Rename/Cut need the parent directory handle — the root has none we can
    // reach (and no directory handle exposes its own parent), so they're off.
    // All four move-shaped actions (plus Generate-thumbnails) are gated on
    // !thumbJob: ensureThumb holds getFile()/stream reads open for seconds on
    // a video, and a move racing that job over the same subtree can fail
    // moveDir/moveFile on a real filesystem that refuses to remove/rename a
    // file with an open reader. No other gate is needed — a menu can't even
    // be opened while an exclusive op is running (the overlay blocks the
    // click/long-press that would open it), so reaching any of these while
    // one is in flight is impossible by construction, not by enumeration.
    const locked = thumbJob;
    addItem('pencil', 'Rename…', !isRoot && !locked, () => doRename(node));
    addItem('scissors', 'Cut', !isRoot && !locked, () => doCut(node));
    // No pasting a folder onto itself / into its own subtree (isPrefix covers
    // both) or back into its current parent (a no-op).
    addItem('clipboard-paste', 'Paste', !!cutPath && !locked
      && !isPrefix(cutPath, node._path)
      && !samePath(cutPath.slice(0, -1), node._path),
      () => doPaste(node));
    menu.appendChild(document.createElement('hr'));
    const n = selection.cells().length;
    addItem('folder-input', `Move ${n} selected here`, n > 0 && !samePath(node._path, state.path) && !locked,
      () => moveSelectedTo(node));
    menu.appendChild(document.createElement('hr'));
    addItem('image', 'Generate thumbnails', !thumbJob, () => generateThumbs(node));
    addItem('folder-plus', 'New folder…', true, () => doNewFolder(node));
  }

  function openMenu(node, x, y) {
    buildMenu(node);
    menu.hidden = false;
    menu.style.left = Math.max(4, Math.min(x, innerWidth - menu.offsetWidth - 8)) + 'px';
    menu.style.top = Math.max(4, Math.min(y, innerHeight - menu.offsetHeight - 8)) + 'px';
  }

  treeBody.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const row = e.target.closest('.trow');
    if (row) openMenu(row.parentElement, e.clientX, e.clientY);
    else closeMenu();
  });

  // Touch long-press fallback for browsers that don't synthesize contextmenu.
  // Local and tiny on purpose: unlike the grid there's no Sortable here to
  // coordinate with, so selection.js's heavier primitive isn't needed. One
  // persistent pointermove listener (gated on a live timer), not one per
  // press — per-press listeners with these cancel paths leak.
  let pressTimer = null, pressAt = null;
  treeBody.addEventListener('pointerdown', (e) => {
    suppressClick = false;
    clearTimeout(pressTimer); pressTimer = null;
    if (e.pointerType !== 'touch' || !e.isPrimary) return;
    const row = e.target.closest('.trow');
    if (!row) return;
    pressAt = { x: e.clientX, y: e.clientY };
    pressTimer = setTimeout(() => {
      pressTimer = null;
      suppressClick = true;
      openMenu(row.parentElement, pressAt.x, pressAt.y);
    }, LONG_PRESS_MS);
  });
  treeBody.addEventListener('pointermove', (e) => {
    if (!pressTimer || Math.hypot(e.clientX - pressAt.x, e.clientY - pressAt.y) <= MOVE_TOL) return;
    clearTimeout(pressTimer); pressTimer = null;   // finger travelled — it's a scroll, not a press
  });
  // A long-press's trailing click (if the browser sends one — it arrives right
  // after pointerup) must not navigate; but the flag can't wait for a click
  // that may never come, so it expires shortly after the pointer lifts.
  const cancelPress = () => {
    clearTimeout(pressTimer); pressTimer = null;
    if (suppressClick) setTimeout(() => { suppressClick = false; }, 150);
  };
  treeBody.addEventListener('pointerup', cancelPress);
  treeBody.addEventListener('pointercancel', cancelPress);

  // Outside-click / Escape close, settings-popover style. Capture phase so the
  // Escape can't also reach app.js's preview-clear handler.
  addEventListener('click', (e) => {
    if (!menu.hidden && !menu.contains(e.target)) closeMenu();
  }, true);
  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!menu.hidden) { closeMenu(); e.stopPropagation(); return; }
    if (cutPath) { clearCut(); e.stopPropagation(); }
  }, true);

  /* ---------- Cut / paste ---------- */
  // The pending cut is a path, not a node reference — rescans rebuild node
  // objects, and the path re-resolves to live handles at paste time.
  let cutPath = null;

  function clearCut() {
    cutPath = null;
    treeBody.querySelector('.trow.cut')?.classList.remove('cut');
  }
  function doCut(node) {
    clearCut();
    cutPath = node._path;
    rowOf(node).classList.add('cut');
  }

  // The already-built .tnode at `path`, or null (an unscanned ancestor means
  // the node was never materialized — callers treat that as "nothing to
  // refresh/expand").
  function nodeAt(path) {
    let node = rootNode;
    for (const seg of path) {
      node = node && [...kidsOf(node).children].find(k => k._name === seg);
    }
    return node ?? null;
  }
  async function refreshAt(path) {
    const n = nodeAt(path);
    if (n) await refreshNode(n);
    return nodeAt(path);   // re-resolve: the refresh rebuilt child node objects
  }

  // Shared skeleton for doPaste/doRename: both are "run one moveDir, then
  // refresh the tree, repoint the current folder if it moved, and report —
  // or, on failure, rebuild the tree from the true on-disk state and recover
  // the current folder as best we can," all inside one withExclusiveOp scope
  // (the post-move refresh/repoint/recover is covered too — no window where
  // the disk moved but the tree/grid still describe the old layout).
  // `move(pathRef, onProgress)` does the op-specific resolve + moveDir call;
  // it sets `pathRef.newPath` and returns true once it actually moved
  // something, or returns false (having already reported its own status) for
  // a validation failure that touched nothing — that path skips
  // refresh/repointCurrent/rebuild entirely, only a thrown error (something
  // failed mid-move) triggers the recovery branch.
  async function runMoveOp({ oldPath, move, refresh, okMsg, failMsg }) {
    const pathRef = { newPath: oldPath };
    await withExclusiveOp(async () => {
      try {
        setStatus('Moving…', 'saving');
        const moved = await move(pathRef, (n) => setStatus(`Moving… ${n}`, 'saving'));
        if (!moved) return;
        await refresh();
        await repointCurrent(oldPath, pathRef.newPath);
        setStatus(okMsg);
      } catch (e) {
        console.error(e);
        setStatus(failMsg, 'error');
        await rebuild();
        await recoverCurrent(oldPath, pathRef.newPath);
      }
    });
  }

  async function doPaste(destNode) {
    const srcPath = cutPath;
    const destPath = destNode._path;
    const srcName = srcPath[srcPath.length - 1];
    clearCut();
    await runMoveOp({
      oldPath: srcPath,
      move: async (pathRef, onProgress) => {
        const srcParent = await resolvePath(state.root, srcPath.slice(0, -1));
        if (!srcParent) throw new Error('cut folder is gone');
        const destName = await uniqueName(destNode._dir, srcName);
        pathRef.newPath = [...destPath, destName];
        await moveDir(srcParent, srcName, destNode._dir, destName, onProgress);
        return true;
      },
      refresh: async () => {
        await refreshAt(srcPath.slice(0, -1));
        const dn = await refreshAt(destPath);   // re-lookup: the source refresh may have rebuilt it
        if (dn) setExpanded(dn, true);
      },
      okMsg: 'Moved ✓', failMsg: 'Move failed — folder left partially moved',
    });
  }

  /* ---------- Folder ops ---------- */
  // Styled folder-name dialog — the bulk-rename dialog's twin (.dlg), used for
  // new-folder creation and folder rename. Live-validates like renameAll's
  // preview: submit stays
  // disabled while the name is empty/illegal, so the resolve only ever yields a
  // valid trimmed name (or null on Cancel/Esc). Adds `no-scroll` to the grid
  // for the modal's lifetime, exactly like the bulk dialog.
  const folderDlg = document.getElementById('folderDlg');
  const folderForm = document.getElementById('folderForm');
  const folderInput = document.getElementById('folderInput');
  const folderErr = document.getElementById('folderErr');
  const folderTitle = document.getElementById('folderDlgTitle');
  const folderOk = document.getElementById('folderOk');
  let folderResolve = null;

  function folderNameError(raw) {
    const name = raw.trim();
    if (!name) return 'Enter a folder name.';
    if (BAD_NAME.test(name)) return 'The name contains characters not allowed in folder names.';
    if (name.startsWith('.')) return 'The name must not start with a dot — that would make the folder hidden.';
    return null;
  }
  function updateFolderValid() {
    const err = folderNameError(folderInput.value);
    folderErr.hidden = !err;
    folderErr.textContent = err || '';
    folderOk.disabled = !!err;
  }
  folderInput.addEventListener('input', updateFolderValid);
  folderForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (folderNameError(folderInput.value)) return;
    const name = folderInput.value.trim();
    const r = folderResolve; folderResolve = null;
    folderDlg.close();
    r?.(name);
  });
  document.getElementById('folderCancel').onclick = () => folderDlg.close();
  folderDlg.addEventListener('close', () => {
    grid.classList.remove('no-scroll');
    const r = folderResolve; folderResolve = null;
    r?.(null);
  });

  function askFolderName({ title, okLabel, initial }) {
    folderTitle.textContent = title;
    folderOk.textContent = okLabel;
    folderInput.value = initial;
    updateFolderValid();
    grid.classList.add('no-scroll');
    folderDlg.showModal();
    folderInput.select();
    return new Promise((resolve) => { folderResolve = resolve; });
  }

  // Re-open the current folder after a rename/move rewrote (part of) its path.
  // No-op navigation still re-runs setCurrent so a rebuilt tree re-highlights.
  async function repointCurrent(oldP, newP) {
    const mapped = remapPath(state.path, oldP, newP);
    if (samePath(mapped, state.path)) { await setCurrent(state.path); return; }
    const h = await resolvePath(state.root, mapped);
    await openDir(h ?? state.root, h ? mapped : []);
  }
  // Failure variant: the move stopped partway, so try the destination path
  // first (the current folder may already have moved), then the old one, then
  // give up and open the root.
  async function recoverCurrent(oldP, newP) {
    for (const cand of [remapPath(state.path, oldP, newP), state.path]) {
      const h = await resolvePath(state.root, cand);
      if (h) { await openDir(h, cand); return; }
    }
    await openDir(state.root, []);
  }

  /* ---------- Move selected media into a tree folder ---------- */
  // If the destination's .XnViewSort EXISTS, the arrivals go to its end (their
  // grid order preserved), after dropping any stale lines with the same names
  // — openFolder's dedup takes the FIRST occurrence, so a leftover mid-file
  // line would otherwise win over the appended one. If it does NOT exist, it
  // stays absent: a sort file listing only the moved names would wrongly pin
  // them first, while absent they natural-sort with the folder's other files.
  async function appendToDestSort(destDir, movedNames) {
    if (!movedNames.length) return;
    let fh;
    try { fh = await destDir.getFileHandle(SORT_FILE); } catch { return; }
    try {
      const lines = (await (await fh.getFile()).text()).split(/\r?\n/).filter(l => l.length);
      if (lines[0]?.trim() === HEADER) lines.shift();
      const movedSet = new Set(movedNames);
      const kept = lines.filter(n => !movedSet.has(n));
      const w = await fh.createWritable();
      await w.write(HEADER + '\n' + kept.concat(movedNames).join('\n') + '\n');
      await w.close();
    } catch (e) {
      console.error('destination sort file update failed', e);
    }
  }

  async function moveSelectedTo(destNode) {
    await withExclusiveOp(async () => {
      const destDir = destNode._dir;
      const srcDir = state.dir;          // snapshot — never re-read mid-loop: a stray
                                         // navigation must not retarget the fallback's removeEntry
      const cells = selection.cells();   // grid DOM order
      clearPreview();                    // the preview may hold a cell about to detach
      purgeDragState();                  // cells still in Sortable's multi-drag list would be
                                         // resurrected into the grid by the next drop once detached
      const moved = [];
      let renamed = 0;
      let summary = '', failed = false;
      try {
        for (let i = 0; i < cells.length; i++) {
          setStatus(`Moving ${i + 1}/${cells.length}…`, 'saving');
          const item = cells[i]._item;
          const finalName = await uniqueName(destDir, item.name);
          if (finalName !== item.name) renamed++;
          const nh = await moveFile(item.handle, srcDir, destDir, finalName);
          // Always migrate the persistent thumb — even under the same name, a
          // copy+delete move changed the mtime and with it the cache key.
          moveThumb(item.name, finalName, nh);
          cells[i].remove();
          moved.push(finalName);
        }
        summary = `Moved ${moved.length} ✓` + (renamed ? ` (${renamed} renamed)` : '');
      } catch (e) {
        console.error(e);
        failed = true;
        summary = `Move failed after ${moved.length}/${cells.length}`;
      } finally {
        // Success or partial failure alike: what moved is gone from the grid —
        // rewrite the source order from the DOM (explicit + awaited, replacing
        // the old deferred scheduleSave), record the arrivals, drop history
        // snapshots that still reference the departed names, and set the final
        // status — all still inside the scope, so nothing outside can stomp it.
        selection.clear();
        await appendToDestSort(destDir, moved);
        await writeSortFile(srcDir, [...grid.children].map(c => c.dataset.name));
        resetHistory();
        setStatus(summary, failed ? 'error' : '');
      }
    });
  }

  async function doNewFolder(node) {
    const name = await askFolderName({ title: 'New folder', okLabel: 'Create', initial: '' });
    if (name == null) return;
    await withExclusiveOp(async () => {
      try {
        if (await nameTaken(node._dir, name)) { setStatus('Name already exists', 'error'); return; }
        await node._dir.getDirectoryHandle(name, { create: true });
        await refreshNode(node);
        setExpanded(node, true);
        await setCurrent(state.path);   // a refresh under the current path drops highlights
        setStatus('Folder created ✓');
      } catch (e) {
        console.error(e);
        setStatus('Create failed', 'error');
        await rebuild();
      }
    });
  }

  async function doRename(node) {
    const oldName = node._name;
    const name = await askFolderName({ title: 'Rename folder', okLabel: 'Rename', initial: oldName });
    if (name == null || name === oldName) return;
    const oldPath = node._path;
    const newPath = [...oldPath.slice(0, -1), name];
    // A pending cut's path may run through the folder being renamed — after
    // the move it would resolve to nothing (or, worse, to a different folder
    // later created under the old name). Simplest safe answer: drop it.
    clearCut();
    await runMoveOp({
      oldPath,
      move: async (pathRef, onProgress) => {
        const parent = await resolvePath(state.root, oldPath.slice(0, -1));
        if (!parent) throw new Error('parent folder is gone');
        // Also refuses case-only renames on case-insensitive filesystems —
        // deliberately: there moveDir would be copying the directory onto
        // itself through the fallback (then deleting it). Don't "fix" this.
        if (await nameTaken(parent, name)) { setStatus('Name already exists', 'error'); return false; }
        pathRef.newPath = newPath;
        await moveDir(parent, oldName, parent, name, onProgress);
        return true;
      },
      refresh: async () => { const pn = parentNodeOf(node); if (pn) await refreshNode(pn); },
      okMsg: 'Renamed ✓', failMsg: 'Rename failed — folder left partially moved',
    });
  }

  /* ---------- The API app.js drives ---------- */
  // (Re)build the tree for a newly picked/reopened root. The root's one-level
  // scan doubles as the "does this folder have subdirectories at all?" check
  // that decides whether the toggle button (and thus the panel) exists.
  async function setRoot(root) {
    closeMenu();   // a menu or pending cut on the previous tree's nodes is stale
    clearCut();
    if (thumbJob) { thumbJob.cancelled = true; thumbJob.silent = true; }   // dead root's files; don't stomp the new root's status either
    rootNode = makeNode(root.name, root, []);
    treeBody.replaceChildren(rootNode);
    const n = await scanNode(rootNode);
    setExpanded(rootNode, true);
    treeBtn.hidden = n === 0;
    applyTreeShow();
  }

  // Expand ancestors along `path` (scanning lazily as needed) and highlight
  // the deepest node found — the current folder, or its nearest surviving
  // ancestor if a segment no longer resolves.
  async function setCurrent(path) {
    if (!rootNode) return;
    let node = rootNode;
    for (const seg of path) {
      if (!node._scanned) await scanNode(node);
      setExpanded(node, true);
      const kid = [...kidsOf(node).children].find(k => k._name === seg);
      if (!kid) break;
      node = kid;
    }
    treeBody.querySelector('.trow.cur')?.classList.remove('cur');
    rowOf(node).classList.add('cur');
  }

  // Rescan the whole tree from the root — the recovery path after any failed
  // filesystem operation, so the panel reflects the true on-disk state.
  async function rebuild() {
    if (!state.root) return;
    await setRoot(state.root);
    await setCurrent(state.path);
  }

  /* ---------- Panel toggle + width ---------- */
  function applyTreeShow() {
    const show = settings.get('treeShow') && !treeBtn.hidden;
    treeEl.classList.toggle('show', show);
    treeBtn.classList.toggle('active', show);
  }
  treeBtn.onclick = () => { settings.set('treeShow', !settings.get('treeShow')); applyTreeShow(); };
  settings.onChange('treeShow', applyTreeShow);

  function applyTreeW(frac) { document.documentElement.style.setProperty('--tree-w', frac * 100 + 'vw'); }
  applyTreeW(settings.get('treeFrac'));
  settings.onChange('treeFrac', applyTreeW);

  let dividerDragging = false, dividerPtrId = null;
  treeDivider.addEventListener('pointerdown', (e) => {
    dividerDragging = true; dividerPtrId = e.pointerId;
    treeDivider.classList.add('dragging');
    try { treeDivider.setPointerCapture(e.pointerId); } catch {}
    e.stopPropagation();
  });
  treeDivider.addEventListener('pointermove', (e) => {
    if (!dividerDragging || e.pointerId !== dividerPtrId) return;
    // The tree is always leftmost, so its left edge is the stable one.
    const w = e.clientX - treeEl.getBoundingClientRect().left;
    const frac = Math.min(TREE_FRAC_MAX, Math.max(TREE_FRAC_MIN, w / innerWidth));
    applyTreeW(frac);
  });
  function endDivider(e) {
    if (!dividerDragging || e.pointerId !== dividerPtrId) return;
    dividerDragging = false;
    treeDivider.classList.remove('dragging');
    const cur = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tree-w')) / 100;
    settings.set('treeFrac', cur);
  }
  treeDivider.addEventListener('pointerup', endDivider);
  treeDivider.addEventListener('pointercancel', endDivider);
  treeDivider.addEventListener('dblclick', () => {
    applyTreeW(TREE_FRAC_DEFAULT);
    settings.set('treeFrac', TREE_FRAC_DEFAULT);
  });

  /* ---------- Recursive thumbnail generation ---------- */
  // Walks the folder's whole subtree, then pushes every media file through
  // ensureThumb (persistent-store only — never the name-keyed session map,
  // which belongs to the current folder). Pool of 2 workers: enough to keep
  // the shared generation queue fed without starving the viewport's own
  // entries, which its picker prioritises anyway. The floating fob shows
  // progress and doubles as the cancel button; the flag is checked between
  // files, so in-flight generations just finish.
  const thumbJobEl = document.getElementById('thumbJob');
  const thumbJobText = document.getElementById('thumbJobText');
  let thumbJob = null;

  async function collectMedia(dir, out) {
    const entries = [];
    for await (const e of dir.entries()) entries.push(e);
    for (const [name, h] of entries) {
      if (h.kind === 'file') {
        if (IMG.test(name) || VID.test(name)) out.push({ name, handle: h, kind: VID.test(name) ? 'video' : 'image' });
      } else {
        await collectMedia(h, out);
      }
    }
    return out;
  }

  async function generateThumbs(node) {
    const job = thumbJob = { cancelled: false };
    thumbJobText.textContent = '…';
    thumbJobEl.hidden = false;
    try {
      const items = await collectMedia(node._dir, []);
      let i = 0, done = 0, failed = 0;
      thumbJobText.textContent = `0/${items.length}`;
      await Promise.all([0, 1].map(async () => {
        while (!job.cancelled && i < items.length) {
          const item = items[i++];
          try { await ensureThumb(item); } catch { failed++; }   // unreadable file — skip, keep going
          thumbJobText.textContent = `${++done}/${items.length}`;
        }
      }));
      if (job.cancelled) { if (!job.silent) setStatus('Thumbnails cancelled'); }
      else setStatus('Thumbnails ✓' + (failed ? ` (${failed} failed)` : ''));
    } catch (e) {
      console.error(e);
      setStatus('Thumbnail generation failed', 'error');
    } finally {
      if (thumbJob === job) { thumbJob = null; thumbJobEl.hidden = true; }
    }
  }
  thumbJobEl.onclick = () => { if (thumbJob) thumbJob.cancelled = true; };

  return { setRoot, setCurrent, rebuild, isThumbBusy: () => !!thumbJob };
}
